import { useEffect, useRef, useCallback } from 'react'

import { toast } from '@buffer-mono/popcorn'

import { useCommentsChannels } from '~publish/components/CommentsSidebar/useCommentsChannels'
import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

import { isChannelEligibleForAutoBackfill } from './isChannelEligibleForAutoBackfill'
import { useForceCommentsBackfill } from './useForceCommentsBackfill'
import { useBackfillCompletion } from '../../BackfillContext'
import { useCommunityConfig } from '../../CommunityConfig'
import {
  setBackfillLoadingCookie,
  clearBackfillLoadingCookie,
  isBackfillLoading,
  getBackfillStartTime,
} from '../../util/backfillCookies'

type BackfillEventData = {
  channelId: string
  message?: string
  newCommentsCount?: number
}

/**
 * Combined hook that handles:
 * 1. Auto-triggering backfill for polling channels on page load
 * 2. Listening to Pusher events globally and clearing cookies
 * 3. Refetching comments when backfill completes
 *
 * This hook should be used at the CommentsRouteHandler level to ensure
 * Pusher events are received even when individual channel pages are unmounted.
 * It is also used in the LastSyncActions component to force a backfilling manually.
 */
export function useCommentsBackfill(): void {
  // Get channels and config internally
  const { channels, loading: channelsLoading } = useCommentsChannels()
  const { config, loading: configLoading } = useCommunityConfig()
  const {
    markChannelCompleted,
    clearChannelCompleted,
    markChannelInitiated,
    markChannelFirstSync,
  } = useBackfillCompletion()

  const hasTriggeredBackfill = useRef(false)
  const { forceBackfill } = useForceCommentsBackfill()
  const currentOrganization = useCurrentOrganization()
  const organizationId = currentOrganization?.id

  // Continuously check for missed Pusher events (runs whenever channels data updates)
  useEffect(() => {
    if (channelsLoading || channels.length === 0) {
      return
    }

    // Check channels with existing backfill cookies
    // to detect if backfill completed while browser was inactive
    channels.forEach((channel) => {
      if (isBackfillLoading(channel.id)) {
        const cookieStartTime = getBackfillStartTime(channel.id)
        const lastCheckedAt = channel.comments?.lastCheckedAt

        // Check if backfill completed while browser was inactive
        // by comparing lastCheckedAt with cookie start time
        if (lastCheckedAt && cookieStartTime) {
          const lastCheckedTimestamp = new Date(lastCheckedAt).getTime()

          // If lastCheckedAt is more recent than cookie start, backfill completed
          if (lastCheckedTimestamp > cookieStartTime) {
            // Backfill succeeded but we missed the Pusher event
            // User was not actively watching, so just clear the cookie
            // Don't show success banner - the updated lastSyncAt is enough
            clearBackfillLoadingCookie(channel.id)
            return
          }
        }

        // Backfill still in progress - mark as initiated
        // so the loading banner shows for this channel
        markChannelInitiated(channel.id)
      }
    })
  }, [channels, channelsLoading, markChannelInitiated])

  // Auto-trigger backfill for polling channels on page load
  useEffect(() => {
    if (
      hasTriggeredBackfill.current ||
      channelsLoading ||
      configLoading ||
      !organizationId ||
      channels.length === 0
    ) {
      return
    }

    const pollingChannels = channels.filter((channel) =>
      isChannelEligibleForAutoBackfill(channel, config, isBackfillLoading),
    )

    if (pollingChannels.length === 0) {
      hasTriggeredBackfill.current = true
      return
    }

    hasTriggeredBackfill.current = true
    const channelIds = pollingChannels.map((channel) => channel.id)

    // Channels being backfilled for the first time (no prior `lastCheckedAt`) —
    // the just-connected case that drives the empty-state explainer card.
    const firstSyncChannelIds = new Set(
      pollingChannels
        .filter((channel) => !channel.comments?.lastCheckedAt)
        .map((channel) => channel.id),
    )

    // Trigger backfill
    const triggerBackfill = async (): Promise<void> => {
      const result = await forceBackfill({
        channelIds,
        organizationId,
        userForced: false,
      })

      if (!result.success) {
        // Backfill failed completely, no channels were initiated
        return
      }

      // Mark only the channels that were actually backfilled as initiated
      // and set cookies only for those channels
      result.channelIds.forEach((channelId) => {
        markChannelInitiated(channelId)
        clearChannelCompleted(channelId)
        setBackfillLoadingCookie(channelId)
        if (firstSyncChannelIds.has(channelId)) {
          markChannelFirstSync(channelId)
        }
      })
    }

    void triggerBackfill()
    // Note: config/forceBackfill are intentionally not in deps to avoid running on every change
    // This effect should only run once when the page loads and data is ready
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, channelsLoading, configLoading, organizationId])

  // Handle successful backfill completion
  const handleBackfillDone = useCallback(
    (data: BackfillEventData) => {
      // Mark channel as completed with message (received Pusher event)
      markChannelCompleted(data.channelId, data.message ?? '')

      // Clear cookie for the completed channel
      clearBackfillLoadingCookie(data.channelId)
    },
    [markChannelCompleted],
  )

  usePusherEvent(PusherEvent.CHANNEL_BACKFILL_DONE, handleBackfillDone)

  // Handle backfill errors
  const handleBackfillError = useCallback(
    (data: BackfillEventData) => {
      // Show error toast to user
      toast.error(
        data.message || 'Failed to sync comments. Please try again.',
        { duration: 4_000 },
      )

      // Mark channel as completed with empty message (error case - no success UI)
      markChannelCompleted(data.channelId, '')

      // Clear cookie for the failed channel
      clearBackfillLoadingCookie(data.channelId)
    },
    [markChannelCompleted],
  )

  usePusherEvent(PusherEvent.CHANNEL_BACKFILL_ERROR, handleBackfillError)
}
